const STUDIO_PRODUCTS_PATH = '/studio/products';

export const STUDIO_PRODUCT_QUERY_PARAM = 'product';
export const STUDIO_CATEGORY_QUERY_PARAM = 'category';

export type StudioProductsSearchParams = {
  product: string | null;
  category: string | null;
};

export type ParsedLegacyStudioProductsRoute = {
  /** Path after `/products/` — empty for listing. */
  rest: string;
  /** Present only for legacy `/studio/[locale]/products/...` URLs. */
  locale?: string;
};

export function buildStudioProductsListingPath(): string {
  return STUDIO_PRODUCTS_PATH;
}

export function buildStudioProductDetailPath(productMetaUrl: string): string {
  const params = new URLSearchParams();
  params.set(STUDIO_PRODUCT_QUERY_PARAM, productMetaUrl);
  return `${STUDIO_PRODUCTS_PATH}?${params.toString()}`;
}

export function buildStudioProductsCategoryPath(categoryMetaUrl: string): string {
  const params = new URLSearchParams();
  params.set(STUDIO_CATEGORY_QUERY_PARAM, categoryMetaUrl);
  return `${STUDIO_PRODUCTS_PATH}?${params.toString()}`;
}

export function parseStudioProductsSearchParams(
  searchParams: Pick<URLSearchParams, 'get'>,
): StudioProductsSearchParams {
  const product = searchParams.get(STUDIO_PRODUCT_QUERY_PARAM)?.trim() || null;
  const category = searchParams.get(STUDIO_CATEGORY_QUERY_PARAM)?.trim() || null;
  return { product, category };
}

export function getStudioProductsRouteKind(
  params: StudioProductsSearchParams,
): 'listing' | 'category' | 'detail' {
  if (params.category) return 'category';
  if (params.product) return 'detail';
  return 'listing';
}

export function parseLegacyStudioProductsPath(
  pathname: string,
  supportedLocales: Set<string>,
): ParsedLegacyStudioProductsRoute | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] !== 'studio') {
    return null;
  }

  if (segments[1] === 'products') {
    return {
      rest: segments.slice(2).join('/'),
    };
  }

  const locale = segments[1]?.trim().toLowerCase();
  if (locale && supportedLocales.has(locale) && segments[2] === 'products') {
    return {
      locale,
      rest: segments.slice(3).join('/'),
    };
  }

  return null;
}

export function legacyPathToStudioProductsUrl(rest: string): string {
  if (!rest) {
    return buildStudioProductsListingPath();
  }

  if (rest.startsWith('categories/')) {
    const categoryMetaUrl = decodeURIComponent(rest.slice('categories/'.length));
    return buildStudioProductsCategoryPath(categoryMetaUrl);
  }

  return buildStudioProductDetailPath(decodeURIComponent(rest));
}

export function isLegacyStudioProductsLocaleRoute(
  route: ParsedLegacyStudioProductsRoute | null,
): route is ParsedLegacyStudioProductsRoute & { locale: string } {
  return Boolean(route?.locale);
}

export function isStudioProductsPreviewPath(pathname: string): boolean {
  return (
    pathname === '/studio/products' ||
    pathname === '/studio/products/' ||
    pathname.startsWith('/studio/products/') ||
    /^\/studio\/[^/]+\/products(?:\/|$)/.test(pathname)
  );
}

export function isStudioProductsPreviewHref(href: string): boolean {
  const [path] = href.split(/[?#]/);
  return isStudioProductsPreviewPath(path);
}

export function toStudioCategoryHref(storefrontCategoryPath: string): string | null {
  const match = storefrontCategoryPath.match(/^\/products\/(.+)$/);
  if (!match?.[1]) return null;
  return buildStudioProductsCategoryPath(match[1]);
}
